import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';

// Get URL language parameter
const getUrlLanguage = () => {
  if (typeof window !== 'undefined') {
    // Skip language detection for admin routes
    if (window.location.pathname.startsWith('/admin')) {
      return null;
    }
    
    // Get the first part of the path after the domain
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    // Check if it's a supported language code
    const supportedLangs = ['en', 'es', 'de', 'it', 'cs'];
    if (supportedLangs.includes(firstSegment)) {
      return firstSegment;
    }
  }
  return null;
};

// Get browser language or fallback to 'en'
const getBrowserLanguage = () => {
  const browserLang = navigator.language.split('-')[0];
  const supportedLangs = ['en', 'es', 'de', 'it', 'cs'];
  return supportedLangs.includes(browserLang) ? browserLang : 'en';
};


i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(HttpApi)
  .init({
    supportedLngs: ['en', 'es', 'de', 'it', 'cs'],
    fallbackLng: 'en',
    ns: ['translation', 'release-notes', 'troubleshooting', 'faq', 'cookie-policy', 'legal'],
    defaultNS: 'translation',
    load: 'languageOnly', // Load only language code (e.g., 'en' instead of 'en-US')
    interpolation: {
      escapeValue: false
    },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      allowMultiLoading: false
    },
    detection: {
      order: ['path', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupFromPathIndex: 0,
      lookupLocalStorage: 'i18nextLng',
      convertDetectedLanguage: (lng: string) => {
        const map: Record<string, string> = {
          'en-US': 'en', 'en-GB': 'en',
          'es-ES': 'es', 'es-MX': 'es',
          'de-DE': 'de', 'de-AT': 'de', 'de-CH': 'de',
          'it-IT': 'it',
          'cs-CZ': 'cs',
        };
        return map[lng] || lng.split('-')[0];
      },
      htmlTag: document.documentElement,
    },
  }).then(() => {
    // Set initial language based on browser preference or stored value
    const storedLang = localStorage.getItem('i18nextLng');
    const urlLang = getUrlLanguage();
    
   // Prioritize URL language over stored language for better consistency with URL
   const initialLang = urlLang || storedLang || getBrowserLanguage();
    
    // Change to detected language
    i18n.changeLanguage(initialLang);
    
    // If URL doesn't have language prefix, add it
    if (!urlLang && typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      
      // Skip redirection for admin routes
      if (currentPath.startsWith('/admin')) {
        return;
      }
      
      const pathSegments = currentPath.split('/').filter(Boolean);
      
      // Check if the first segment is already a language code
      const supportedLangs = ['en', 'es', 'de', 'it', 'cs'];
      const hasLangPrefix = pathSegments.length > 0 && supportedLangs.includes(pathSegments[0].toLowerCase());
      
      if (!hasLangPrefix && currentPath !== '/') {
        // Add language prefix to current path
        const newPath = `/${initialLang}${currentPath}`;
        window.history.replaceState({}, '', newPath);
      } else if (!hasLangPrefix && currentPath === '/') {
        // Redirect root to language-prefixed root
        const newPath = `/${initialLang}`;
        window.history.replaceState({}, '', newPath);
      }
    }
    // If URL has a language prefix that doesn't match stored preference, update URL
   // Remove this section to prevent URL language from being overridden by stored preference
   // This ensures the URL language is always respected
   else if (urlLang && !window.location.pathname.startsWith('/admin')) {
     // Update stored language to match URL language for consistency
     if (storedLang !== urlLang) {
       localStorage.setItem('i18nextLng', urlLang);
     }
    }
  }).then(() => {
    // Set initial document language
    document.documentElement.lang = i18n.language;
    
    // Add language change listener
    i18n.on('languageChanged', (lng: string) => {
      document.documentElement.lang = lng;
      
    });
  });

export default i18n;
